import { useEffect, useState } from "react";

/**
 * Which section owns the viewport right now. One IntersectionObserver
 * over a thin band across the viewport's middle (no scroll-event math);
 * whichever section crosses the band is active, and when two touch it
 * at a boundary the further-down one wins. jsdom has no
 * IntersectionObserver, so the guard leaves tests (and any ancient
 * browser) at the entry state.
 */
export function useActiveSection(ids: ReadonlyArray<string>, initial = ""): string {
  const [active, setActive] = useState<string>(initial);
  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return;
    const inBand = new Set<string>();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) inBand.add(entry.target.id);
          else inBand.delete(entry.target.id);
        }
        for (let i = ids.length - 1; i >= 0; i--) {
          const id = ids[i];
          if (id && inBand.has(id)) {
            setActive(id);
            return;
          }
        }
      },
      { rootMargin: "-45% 0px -45% 0px" },
    );
    for (const id of ids) {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    }
    return () => observer.disconnect();
  }, [ids]);
  return active;
}
